import { IconName } from '@ui'
import { z } from 'zod'

// Server-side validation constants (must match server)
export const MAX_TEXT_FIELD_SIZE_BYTES = 10 * 1024 // 10KB per field
export const MAX_TEXT_FIELDS_COUNT = 20
export const MAX_MEDIA_COUNT = 20
export const MAX_FAQ_COUNT = 50
export const MAX_FAQ_QUESTION_LENGTH = 500
export const MAX_FAQ_ANSWER_LENGTH = 5000
export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024 // 5MB
export const MAX_VIDEO_SIZE_BYTES = 512 * 1024 * 1024 // 512MB

// Client-side UI constants
export const MIN_FEATURES = 3
export const MAX_FEATURES = 5
export const MAX_FAQS = MAX_FAQ_COUNT // Use server limit
export const MAX_SOCIAL_LINKS = 6
export const MAX_MEDIA_ITEMS = MAX_MEDIA_COUNT // Use server limit

export type TownSocialLinkType = 'x' | 'instagram' | 'website' | 'other'

const mediaVariantSchema = z.object({
    name: z.string(),
    url: z.string().url(),
    width: z.number().optional(),
    height: z.number().optional(),
    posterUrl: z.string().optional(),
})

const imageMediaSchema = z.object({
    key: z.string(),
    type: z.literal('image'),
    variants: z.array(mediaVariantSchema).optional(),
})

const videoMediaSchema = z.object({
    key: z.string(),
    type: z.literal('video'),
    variants: z.array(mediaVariantSchema).optional(),
    muxPlaybackId: z.string().optional(),
})

export const mediaFormSchema = z.discriminatedUnion('type', [imageMediaSchema, videoMediaSchema])

const featureSchema = z.object({
    value: z.string().max(120, 'Feature cannot exceed 120 characters').optional().default(''),
})

// Helper to validate byte size
const validateByteSize = (value: string, maxBytes: number): boolean => {
    return new TextEncoder().encode(value).length <= maxBytes
}

const byteSizeRefinement = (value: string, maxBytes: number) => {
    const byteSize = new TextEncoder().encode(value).length
    return {
        valid: validateByteSize(value, maxBytes),
        byteSize,
        maxBytes,
    }
}

export const faqSchema = z
    .object({
        question: z
            .string()
            .max(
                MAX_FAQ_QUESTION_LENGTH,
                `FAQ question cannot exceed ${MAX_FAQ_QUESTION_LENGTH} characters`,
            )
            .optional()
            .default(''),
        answer: z
            .string()
            .max(
                MAX_FAQ_ANSWER_LENGTH,
                `FAQ answer cannot exceed ${MAX_FAQ_ANSWER_LENGTH} characters`,
            )
            .optional()
            .default(''),
    })
    .superRefine((data, ctx) => {
        const hasQuestion = data.question.trim().length > 0
        const hasAnswer = data.answer.trim().length > 0

        // If one is filled but not the other, show error on the empty field
        if (hasQuestion && !hasAnswer) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please fill in both question and answer',
                path: ['answer'],
            })
        } else if (!hasQuestion && hasAnswer) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please fill in both question and answer',
                path: ['question'],
            })
        }

        // Validate byte sizes
        if (hasQuestion) {
            const questionValidation = byteSizeRefinement(data.question, MAX_TEXT_FIELD_SIZE_BYTES)
            if (!questionValidation.valid) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Question is too large (${Math.ceil(questionValidation.byteSize / 1024)}KB). Maximum ${Math.ceil(questionValidation.maxBytes / 1024)}KB allowed`,
                    path: ['question'],
                })
            }
        }

        if (hasAnswer) {
            const answerValidation = byteSizeRefinement(data.answer, MAX_TEXT_FIELD_SIZE_BYTES)
            if (!answerValidation.valid) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Answer is too large (${Math.ceil(answerValidation.byteSize / 1024)}KB). Maximum ${Math.ceil(answerValidation.maxBytes / 1024)}KB allowed`,
                    path: ['answer'],
                })
            }
        }
    })

// Helper function to validate social media handles that allow dots (e.g. Instagram)
const handleValidator = (value: string) => {
    if (!value || value === '@') {
        return true // Allow empty for optional validation
    }
    // Remove @ if present and validate handle format
    const handle = value.startsWith('@') ? value.slice(1) : value
    return /^[a-zA-Z0-9_.]+$/.test(handle)
}

// Helper function to validate X handles (no dots allowed)
const xHandleValidator = (value: string) => {
    if (!value || value === '@') {
        return true // Allow empty for optional validation
    }
    // Remove @ if present and validate handle format
    const handle = value.startsWith('@') ? value.slice(1) : value
    return /^[A-Za-z0-9_]+$/.test(handle)
}

const websiteSocialLinkSchema = z.object({
    type: z.literal('website'),
    label: z.string().optional(),
    url: z
        .string()
        .optional()
        .default('')
        .refine(
            (val) => !val || z.string().url().safeParse(val).success,
            'Please enter a valid URL (e.g., https://example.com)',
        ),
})

const xSocialLinkSchema = z.object({
    type: z.literal('x'),
    label: z.string().optional(),
    url: z
        .string()
        .optional()
        .default('')
        .refine(
            (val) => !val || xHandleValidator(val),
            'Please enter a valid X handle using only letters, numbers, and underscores (e.g., @username or username)',
        ),
})

const instagramSocialLinkSchema = z.object({
    type: z.literal('instagram'),
    label: z.string().optional(),
    url: z
        .string()
        .optional()
        .default('')
        .refine(
            (val) => !val || handleValidator(val),
            'Please enter a valid Instagram handle (e.g., @username or username)',
        ),
})

const otherSocialLinkSchema = z.object({
    type: z.literal('other'),
    label: z.string().optional(),
    url: z.string().optional().default(''),
})

const socialLinkSchema = z.discriminatedUnion('type', [
    websiteSocialLinkSchema,
    xSocialLinkSchema,
    instagramSocialLinkSchema,
    otherSocialLinkSchema,
])

export const editTownMetadataSchema = z.object({
    name: z
        .string()
        .min(1, 'Town name is required')
        .max(30, 'Town name cannot exceed 30 characters'),
    motto: z
        .string()
        .max(50, 'Town motto cannot exceed 50 characters')
        .optional()
        .default('')
        .refine(
            (val) => !val || validateByteSize(val, MAX_TEXT_FIELD_SIZE_BYTES),
            (val) => ({
                message: `Motto is too large (${Math.ceil(new TextEncoder().encode(val).length / 1024)}KB). Maximum ${Math.ceil(MAX_TEXT_FIELD_SIZE_BYTES / 1024)}KB allowed`,
            }),
        ),
    category: z.string().optional().default(''),
    aboutTown: z.string().max(5000, 'Town description cannot exceed 5000 characters').default(''),
    aboutCreator: z
        .string()
        .max(2000, 'Creator bio cannot exceed 2000 characters')
        .optional()
        .default('')

        .refine(
            (val) => !val || validateByteSize(val, MAX_TEXT_FIELD_SIZE_BYTES),
            (val) => ({
                message: `Creator bio is too large (${Math.ceil(new TextEncoder().encode(val).length / 1024)}KB). Maximum ${Math.ceil(MAX_TEXT_FIELD_SIZE_BYTES / 1024)}KB allowed`,
            }),
        ),
    creatorDisplayName: z
        .string()
        .max(64, 'Creator display name cannot exceed 64 characters')
        .optional()
        .default(''),
    media: z
        .array(mediaFormSchema)
        .max(MAX_MEDIA_ITEMS, `You can upload up to ${MAX_MEDIA_ITEMS} media items`)
        .optional()
        .default([]),
    features: z.array(featureSchema).max(MAX_FEATURES, `Add up to ${MAX_FEATURES} features`),
    faqs: z
        .array(faqSchema)
        .max(MAX_FAQS, `Add up to ${MAX_FAQS} FAQs`)
        .optional()
        .default([{ question: '', answer: '' }]),
    socialLinks: z
        .array(socialLinkSchema)
        .max(MAX_SOCIAL_LINKS, `Add up to ${MAX_SOCIAL_LINKS} social links`)
        .optional()
        .default([]),
})

const baseMediaSchema = z.object({
    key: z.string(),
})

const orderedMediaSchema = z.object({
    key: z.string(),
    order: z.number(),
})

export const serverUploadResultMediaSchema = z.discriminatedUnion('type', [
    baseMediaSchema.extend(imageMediaSchema.shape),
    baseMediaSchema.extend(videoMediaSchema.shape),
])

export const serverMetadataMediaSchema = z.discriminatedUnion('type', [
    orderedMediaSchema.extend(imageMediaSchema.shape),
    orderedMediaSchema.extend(videoMediaSchema.shape),
])
export const serverSpaceMetadataSchema = z.object({
    category: z.string(),
    textFields: z.record(z.string(), z.string()),
    media: z.array(serverMetadataMediaSchema),
    faq: z.array(
        z.object({
            question: z.string(),
            answer: z.string(),
            order: z.number(),
        }),
    ),
})

export type ServerSpaceMetadata = z.infer<typeof serverSpaceMetadataSchema>
export type ServerUploadResultMedia = z.infer<typeof serverUploadResultMediaSchema>

export type TownSocialLink = {
    type: TownSocialLinkType
    label?: string
    url: string
}

export const SOCIAL_LINK_DEFAULTS: Array<{
    type: TownSocialLinkType
    label: string
    placeholder: string
    icon: IconName
}> = [
    {
        type: 'x',
        label: 'X / Twitter',
        placeholder: 'X / Twitter',
        icon: 'twitter',
    },
    {
        type: 'instagram',
        label: 'Instagram',
        placeholder: 'Instagram',
        icon: 'instagram',
    },
    {
        type: 'website',
        label: 'Website',
        placeholder: 'Website',
        icon: 'link',
    },
]

export type FeatureFormValue = z.infer<typeof featureSchema>
export type FAQFormValue = z.infer<typeof faqSchema>
export type SocialLinkFormValue = z.infer<typeof socialLinkSchema>
export type MediaVariantFormValue = z.infer<typeof mediaVariantSchema>
export type MediaFormValue = z.infer<typeof mediaFormSchema>

export type EditTownMetadataFormValues = z.infer<typeof editTownMetadataSchema>

export type FormFallbackValues = {
    fallbackName?: string
    fallbackMotto?: string
    fallbackAboutTown?: string
}
